import {
  functionalUpdate,
  getMemoOptions,
  memo,
  type Row,
  type RowData,
  type RowModel,
  type RowSelectionState,
  type Table,
  type TableFeature,
} from '@tanstack/react-table';

/**
 * With server-side (manual) pagination the table only holds the current page,
 * so TanStack's `getSelectedRowModel()` drops selected rows from other pages
 * even though their ids stay in `rowSelection`.
 *
 * This feature keeps the selected rows of every visited page so
 * `getSelectedRowModel()` returns the whole selection until it is cleared.
 */
export const PersistentRowSelectionFeature: TableFeature = {
  createTable: <TData extends RowData>(table: Table<TData>) => {
    const selectedRowsCache = new Map<string, Row<TData>>();

    const syncCache = (rowSelection: RowSelectionState) => {
      table.getCoreRowModel().flatRows.forEach((row) => {
        if (rowSelection[row.id]) selectedRowsCache.set(row.id, row);
      });
      selectedRowsCache.forEach((_, id) => {
        if (!rowSelection[id]) selectedRowsCache.delete(id);
      });
    };

    // Capture rows at selection time, before the page (and its rows) changes.
    const setRowSelection = table.setRowSelection;
    table.setRowSelection = (updater) =>
      setRowSelection((old) => {
        const next = functionalUpdate(updater, old);
        syncCache(next);
        return next;
      });

    table.getSelectedRowModel = memo(
      () => [table.getState().rowSelection, table.getCoreRowModel()],
      (rowSelection): RowModel<TData> => {
        syncCache(rowSelection);
        const rows = [...selectedRowsCache.values()];
        return {
          rows,
          flatRows: rows,
          rowsById: Object.fromEntries(rows.map((row) => [row.id, row])),
        };
      },
      getMemoOptions(table.options, 'debugTable', 'getSelectedRowModel')
    );
  },
};
